/*
  Sticky add-to-cart bar.

  Reveals only once the real buy block has scrolled ABOVE the viewport, never when it is still
  below — otherwise the bar would cover the page before the shopper has even reached the product
  form, which is worse than not having it.

  Price, variant name and the disabled state are mirrored from the live product form rather than
  tracked separately, so there is one source of truth for the selected variant. Dawn re-renders
  the price node on variant change, hence the MutationObserver.
*/
(function () {
  const bar = document.querySelector('[data-sticky-atc]');
  if (!bar) return;

  const watchSelector = bar.dataset.stickyAtcWatch || '.product-form__buttons';
  const anchor = document.querySelector(watchSelector);
  if (!anchor) return;

  const priceOut = bar.querySelector('[data-sticky-atc-price]');
  const variantOut = bar.querySelector('[data-sticky-atc-variant]');
  const button = bar.querySelector('[data-sticky-atc-button]');
  const root = document.documentElement;

  function syncChatClearance(visible) {
    if (visible) {
      root.style.setProperty(
        '--jewe-sticky-atc-height',
        Math.ceil(bar.getBoundingClientRect().height) + 'px'
      );
      root.classList.add('jewe-sticky-atc-visible');
      return;
    }

    root.classList.remove('jewe-sticky-atc-visible');
    root.style.removeProperty('--jewe-sticky-atc-height');
  }

  function show(visible) {
    bar.classList.toggle('is-visible', visible);
    bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    syncChatClearance(visible);
  }

  /*
    A scroll listener, not an IntersectionObserver.

    IO only fires when a threshold is crossed. On a phone the buy block starts BELOW the fold
    (measured: top 1065 in an 844px viewport), so it is never intersecting — scrolling past it goes
    from "not intersecting, below" to "not intersecting, above" with no crossing in between, and the
    callback never fires again. The bar simply never appeared on mobile, which is the width where it
    matters most. A fling or an in-page anchor jump breaks it on desktop for the same reason.

    Comparing the anchor's own position each frame is deterministic at any scroll speed.
  */
  let ticking = false;
  let lastState = null;

  function evaluate() {
    ticking = false;
    // Revealed once the buy block has fully passed above the top of the viewport.
    const scrolledPast = anchor.getBoundingClientRect().bottom < 0;
    if (scrolledPast !== lastState) {
      lastState = scrolledPast;
      show(scrolledPast);
    } else if (scrolledPast) {
      // Refresh the measured height after viewport rotation or safe-area changes.
      syncChatClearance(true);
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(evaluate);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  evaluate();

  /*
    Everything below re-queries its nodes on each run. Dawn replaces the price node wholesale on a
    variant change, so a MutationObserver bound to the original element would be observing a
    detached node after the first switch.
  */
  const infoContainer = document.querySelector('.product__info-container');

  function syncPrice() {
    const source = document.querySelector('.product__info-container .price');
    if (!source || !priceOut) return;
    // .price carries both regular and sale nodes; take the sale price when the product is on sale.
    const sale = source.querySelector('.price-item--sale');
    const regular = source.querySelector('.price-item--regular');
    const text = ((sale && sale.textContent) || (regular && regular.textContent) || '').trim();
    if (text) priceOut.textContent = text;
  }

  function syncVariant() {
    if (!variantOut) return;
    const checked = document.querySelectorAll(
      ".product-form__input input[type='radio']:checked"
    );
    if (checked.length) {
      variantOut.textContent = Array.from(checked)
        .map(function (i) { return i.value; })
        .join(' \u00b7 ');
      return;
    }
    const select = document.querySelector('.product-form__input select');
    if (select && select.selectedOptions[0]) variantOut.textContent = select.selectedOptions[0].text.trim();
  }

  function syncAvailability() {
    /*
      Re-query the main button every time. Dawn disables it for the duration of the variant fetch
      and re-enables it when the new markup lands; syncing once off a stale reference left the
      sticky button permanently disabled after the first variant change, so it could not be clicked
      at all. Observing the attribute directly is what makes this correct.
    */
    const main = document.querySelector('.product-form__submit');
    if (!button || !main) return;
    button.toggleAttribute('disabled', main.hasAttribute('disabled'));
  }

  function syncAll() {
    syncPrice();
    syncVariant();
    syncAvailability();
  }

  syncAll();

  if (infoContainer && 'MutationObserver' in window) {
    // The sticky bar sits outside .product__info-container, so writing to it cannot retrigger this.
    new MutationObserver(syncAll).observe(infoContainer, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'class'],
    });
  }

  document.addEventListener('change', function (event) {
    if (event.target.closest && event.target.closest('.product-form__input')) syncAll();
  });

})();
